import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { Edge, ReactFlowInstance } from '@xyflow/react';
import { FIT_VIEW_PADDING } from './treemap-constants';
import type { TreemapFlowNode } from './treemap-types';
import {
  getDrillPathTargetId,
  isTreemapViewportRect,
  type TreemapViewportRect,
} from './treemap-view-model';

const INITIAL_FIT_MIN_SIZE = 4;

interface UseTreemapResizeRefitInput {
  canvasWrapperRef: RefObject<HTMLDivElement | null>;
  reactFlowInstance: ReactFlowInstance<TreemapFlowNode, Edge> | null;
  drillPathIds: string[];
  rectById: Map<string, TreemapViewportRect>;
}

function refitToCurrentView(input: {
  reactFlowInstance: ReactFlowInstance<TreemapFlowNode, Edge>;
  drillPathIds: string[];
  rectById: Map<string, TreemapViewportRect>;
  setZoomLevel: (zoomLevel: number) => void;
}): void {
  const targetId = getDrillPathTargetId(input.drillPathIds);
  const rect = targetId ? input.rectById.get(targetId) : undefined;

  if (isTreemapViewportRect(rect)) {
    input.reactFlowInstance.fitBounds(rect, { padding: FIT_VIEW_PADDING });
  } else {
    input.reactFlowInstance.fitView({ padding: FIT_VIEW_PADDING });
  }

  input.setZoomLevel(input.reactFlowInstance.getZoom());
}

export function useTreemapResizeRefit({
  canvasWrapperRef,
  reactFlowInstance,
  drillPathIds,
  rectById,
  setZoomLevel,
}: UseTreemapResizeRefitInput & {
  setZoomLevel: (zoomLevel: number) => void;
}): void {
  const drillPathIdsRef = useRef<string[]>(drillPathIds);
  const rectByIdRef = useRef(rectById);

  useEffect(() => {
    drillPathIdsRef.current = drillPathIds;
  }, [drillPathIds]);

  useEffect(() => {
    rectByIdRef.current = rectById;
  }, [rectById]);

  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper || !reactFlowInstance) {
      return;
    }

    let frame = 0;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const { width, height } = entry.contentRect;
      if (width <= INITIAL_FIT_MIN_SIZE || height <= INITIAL_FIT_MIN_SIZE) {
        return;
      }

      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        refitToCurrentView({
          reactFlowInstance,
          drillPathIds: drillPathIdsRef.current,
          rectById: rectByIdRef.current,
          setZoomLevel,
        }),
      );
    });

    observer.observe(wrapper);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [canvasWrapperRef, reactFlowInstance, setZoomLevel]);
}
